import React, { useRef, forwardRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight, Sparkles, Zap, Shield, ChevronDown } from "lucide-react";
import Image from "../../../components/AppImage";

const HeroSection = forwardRef((props, ref) => {
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };

  const y1 = useSpring(useTransform(scrollYProgress, [0, 1], [0, -100]), springConfig);
  const y2 = useSpring(useTransform(scrollYProgress, [0, 1], [0, 100]), springConfig);
  const opacity = useSpring(useTransform(scrollYProgress, [0, 0.5], [1, 0]), springConfig);
  const scale = useSpring(useTransform(scrollYProgress, [0, 1], [1, 0.9]), springConfig);

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  // Smooth word-by-word reveal with unified highlight
  const AnimatedText = ({ text, gradient = false, lineDelay = 0 }) => {
    const words = text.split(' ');

    return (
      <span className="inline-block">
        {words.map((word, wordIndex) => {
          const wordDelay = lineDelay + wordIndex * 0.15;

          return (
            <span key={wordIndex} className="inline-block mr-3 md:mr-4 relative">
              <motion.span
                className={`relative inline-block ${gradient ? 'bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200 bg-clip-text text-transparent' : ''}`}
                style={{
                  padding: '0.25rem 0',
                  backgroundColor: 'rgba(139, 92, 246, 0.3)',
                  boxShadow: '0.5rem 0 0 rgba(139, 92, 246, 0.3), -0.5rem 0 0 rgba(139, 92, 246, 0.3)',
                  boxDecorationBreak: 'clone',
                  WebkitBoxDecorationBreak: 'clone'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: 1,
                  x: 0
                }}
                transition={{
                  duration: 0.5,
                  delay: wordDelay,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                {word}
              </motion.span>
            </span>
          );
        })}
      </span>
    );
  };


  return (
    <section
      ref={ref}
      className="relative min-h-screen 3xl:!min-h-screen text-white overflow-hidden flex items-center md:mb-6 pb-20 lg:pb-10 xl:pb-10"
    >
      {/* Background Image with Parallax */}
      <motion.div
        style={{ scale: useSpring(useTransform(scrollYProgress, [0, 1], [1, 1.1]), springConfig) }}
        className="absolute inset-0"
      >
        <Image
          src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop"
          alt="Tech Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/95 via-blue-800/90 to-purple-900/95" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-600/20 via-transparent to-transparent" />
      </motion.div>

      {/* Animated grid overlay with parallax */}
      <motion.div
        style={{ y: y1, opacity: 0.1 }}
        className="absolute inset-0"
      >
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#grid)" />
        </svg>
      </motion.div>

      {/* Animated particles/dots */}
      <motion.div className="absolute inset-0 opacity-30">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 1, 0.2],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </motion.div>

      {/* Enhanced floating orbs */}
      <motion.div
        style={{ y: y2, x: useTransform(scrollYProgress, [0, 1], [0, -50]) }}
        className="absolute top-20 left-10 w-96 h-96 bg-cyan-400 rounded-full mix-blend-screen filter blur-3xl opacity-20"
        animate={{
          scale: [1, 1.3, 1],
          rotate: [0, 180, 360]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div
        style={{ y: y1, x: useTransform(scrollYProgress, [0, 1], [0, 50]) }}
        className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl opacity-20"
        animate={{
          scale: [1, 1.4, 1],
          rotate: [360, 180, 0]
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, 80]) }}
        className="absolute top-1/2 left-1/3 w-80 h-80 bg-pink-500 rounded-full mix-blend-screen filter blur-3xl opacity-15"
        animate={{
          scale: [1, 1.2, 1],
          x: [-20, 20, -20]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="container mx-auto relative z-10 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
        <div className="grid lg:grid-cols-[55fr_45fr] xl:grid-cols-[55fr_45fr] gap-6 lg:gap-10 xl:gap-16 3xl:gap-32 5xl:gap-32 items-center mx-auto">
          {/* Left content */}
          <div className="flex flex-col gap-6 sm:gap-10 lg:gap-3 3xl:gap-14 5xl:gap-20 w-full relative z-20 mt-10 lg:mt-0 lg:-mt-10 xl:mt-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                damping: 10,
                stiffness: 100,
                delay: 0.1
              }}
              whileHover={{ scale: 1.05, rotate: 2 }}
              className="mb-2 sm:mb-6 lg:mb-6 xl:mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 self-start mt-12 sm:mt-20 lg:mt-0 xl:mt-20 3xl:px-6 3xl:py-3 5xl:px-8 5xl:py-4"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-4 h-4 text-yellow-300 3xl:w-6 3xl:h-6 5xl:w-8 5xl:h-8" />
              </motion.div>
              <span className="text-sm font-semibold 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl">Transform Your Business</span>
            </motion.div>

            {/* Animated Title with Fluid Typography - Adjusted for safety */}
            <h1 className="font-black leading-[1.1] tracking-tight text-left relative text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-[3.25rem] 2xl:text-6xl 3xl:text-7xl 4xl:text-[5rem] 5xl:text-[7rem] 6xl:text-[9rem]">
              {/* Optional: Add a subtle backdrop for better contrast if overlap happens */}
              <div className="absolute -inset-4 bg-blue-900/20 blur-2xl -z-10 rounded-full opacity-0 lg:opacity-100" />

              <div className="block mb-2">
                <AnimatedText text="Innovative Digital" lineDelay={0.3} />
              </div>
              <div className="block mb-2">
                <AnimatedText text="& AI Solutions for" lineDelay={0.9} />
              </div>
              <div className="block">
                <AnimatedText text="Modern Businesses" lineDelay={1.5} />
              </div>
            </h1>

            <motion.p
              className="text-base sm:text-lg md:text-xl lg:text-base xl:text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl text-blue-100 leading-loose text-left mt-2 mb-6 max-w-2xl 3xl:max-w-3xl 4xl:max-w-4xl 5xl:max-w-5xl 6xl:max-w-7xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 2.2 }}

            >
              <motion.span
                className="font-bold text-white relative inline"
                style={{
                  backgroundColor: 'rgba(251, 191, 36, 0.25)',
                  boxDecorationBreak: 'clone',
                  WebkitBoxDecorationBreak: 'clone',
                  padding: '0 4px',
                  borderRadius: '4px'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: 1,
                  x: 0
                }}
                transition={{ duration: 0.6, delay: 2.5, ease: [0.25, 0.1, 0.25, 1] }}
              >
                Smart automations
              </motion.span>
              {" "}and{" "}
              <motion.span
                className="font-bold text-white relative inline"
                style={{
                  backgroundColor: 'rgba(251, 191, 36, 0.25)',
                  boxDecorationBreak: 'clone',
                  WebkitBoxDecorationBreak: 'clone',
                  padding: '0 4px',
                  borderRadius: '4px'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: 1,
                  x: 0
                }}
                transition={{ duration: 0.6, delay: 2.5, ease: [0.25, 0.1, 0.25, 1] }}
              >
                scalable digital solutions
              </motion.span>   that help your business grow.
            </motion.p>



            <motion.div
              className="flex flex-col sm:flex-row lg:flex-nowrap gap-3 sm:gap-4 3xl:gap-6 5xl:gap-8 w-full sm:w-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 2.8 }}
            >
              <motion.a
                href="/contact-us"
                className="group relative bg-white text-blue-900 font-bold px-4 sm:px-8 py-3.5 sm:py-4 lg:px-5 lg:py-3 3xl:px-10 3xl:py-5 4xl:px-12 4xl:py-6 5xl:px-16 5xl:py-8 text-sm sm:text-base lg:text-sm 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl rounded-xl shadow-xl overflow-hidden inline-flex items-center justify-center gap-2 w-full sm:w-auto text-center border-2 border-transparent whitespace-nowrap"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(255, 255, 255, 0.3)" }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10">Get a Free Automation Audit</span>
                <motion.div
                  className="relative z-10 flex-shrink-0"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 5xl:w-8 5xl:h-8" />
                </motion.div>
              </motion.a>

              <motion.a
                href="/services"
                className="border-2 border-white text-white font-bold px-4 sm:px-8 py-3.5 sm:py-4 lg:px-5 lg:py-3 3xl:px-10 3xl:py-5 4xl:px-12 4xl:py-6 5xl:px-16 5xl:py-8 text-sm sm:text-base lg:text-sm 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl rounded-xl backdrop-blur-md bg-white/5 inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors w-full sm:w-auto text-center whitespace-nowrap"
                whileHover={{
                  scale: 1.05,
                  borderColor: "rgba(255,255,255,1)",
                  backgroundColor: "rgba(255,255,255,0.1)"
                }}
                whileTap={{ scale: 0.98 }}
              >
                Explore Our Services
              </motion.a>
            </motion.div>

            <motion.div
              className="flex flex-wrap gap-4 sm:gap-6 pt-4 5xl:gap-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 3 }}
            >
              {[
                { icon: Zap, text: "24/7 Support" },
                { icon: Shield, text: "100+ Projects" },
                { icon: Sparkles, text: "Expert Team" }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 3 + i * 0.1 }}
                  whileHover={{ scale: 1.1, x: 5 }}
                >
                  <motion.div
                    className="w-8 h-8 3xl:w-10 3xl:h-10 5xl:w-12 5xl:h-12 rounded-full bg-green-400/20 backdrop-blur-sm flex items-center justify-center"
                    animate={{
                      boxShadow: [
                        "0 0 0 0 rgba(74, 222, 128, 0.4)",
                        "0 0 0 10px rgba(74, 222, 128, 0)",
                        "0 0 0 0 rgba(74, 222, 128, 0)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                    whileHover={{ rotate: 360 }}
                  >
                    <item.icon className="w-4 h-4 3xl:w-5 3xl:h-5 5xl:w-6 5xl:h-6 text-green-300" />
                  </motion.div>
                  <span className="text-xs sm:text-sm 3xl:text-base 5xl:text-xl text-blue-100 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right content - Image */}
          <motion.div
            style={{ y: y2, scale }}
            initial={{ opacity: 0, x: 100, rotateY: -30 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{
              duration: 1.2,
              delay: 0.5,
              type: "spring",
              stiffness: 60
            }}
          >
            <motion.div
              className="relative group"
              whileHover={{ scale: 1.02, rotateY: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {/* Animated glow */}
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl blur-xl opacity-70"
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  rotate: { duration: 8, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                }}
              />

              <div className="relative bg-gradient-to-br from-white/5 to-transparent backdrop-blur-sm rounded-3xl overflow-hidden border border-white/10 shadow-2xl"
                style={{
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
              >
                <motion.div
                  className="relative aspect-[5/4]"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                >
                  <Image
                    src="https://images.unsplash.com/photo-1581092921461-39b9d08a9b21?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                    alt="IT Services Team"
                    className="w-full h-full object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 via-transparent to-transparent" />

                  {/* Stats - Premium Design */}
                  <motion.div
                    className="absolute bottom-4 left-4 right-4 flex gap-3 5xl:gap-6 5xl:bottom-8 5xl:left-8 5xl:right-8"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                  >
                    {[
                      { value: "99%", label: "Satisfaction", gradient: "from-blue-500 to-cyan-400" },
                      { value: "24/7", label: "Support", gradient: "from-purple-500 to-pink-400" },
                      { value: "10+", label: "Years", gradient: "from-orange-500 to-yellow-400" }
                    ].map((stat, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 relative group"
                        initial={{ opacity: 0, scale: 0.5, rotateX: -90 }}
                        animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                        transition={{
                          delay: 1.2 + i * 0.1,
                          type: "spring",
                          stiffness: 200,
                          damping: 15
                        }}
                        whileHover={{
                          scale: 1.15,
                          y: -10,
                          zIndex: 10
                        }}
                      >
                        <motion.div
                          className={`absolute -inset-0.5 bg-gradient-to-r ${stat.gradient} rounded-xl blur opacity-0 group-hover:opacity-75 transition duration-300`}
                        />

                        <div className="relative bg-white/98 backdrop-blur-md rounded-xl p-4 5xl:p-6 shadow-xl border border-white/50 overflow-hidden">
                          <motion.div
                            className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-5`}
                            whileHover={{ opacity: 0.1 }}
                          />

                          <motion.div
                            className={`absolute top-2 right-2 w-8 h-8 5xl:w-10 5xl:h-10 bg-gradient-to-br ${stat.gradient} rounded-full opacity-20`}
                            animate={{
                              scale: [1, 1.2, 1],
                              rotate: [0, 180, 360]
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              delay: i * 0.5
                            }}
                          />

                          <motion.div
                            className={`text-2xl 5xl:text-5xl font-black bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 1.4 + i * 0.1, type: "spring" }}
                          >
                            {stat.value}
                          </motion.div>
                          <div className="text-xs 5xl:text-lg text-white font-semibold mt-1 uppercase tracking-wide">{stat.label}</div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToContent}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/70 hover:text-white cursor-pointer group"
        style={{ opacity }}
        whileHover={{ scale: 1.2 }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 3.5 }}
      >
        <div className="relative">
          <motion.div
            className="absolute inset-0 border-2 border-white/30 rounded-full blur-sm"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <div className="relative w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <motion.div
              className="w-1.5 h-3 bg-white rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>
        </div>
        <motion.div
          animate={{ y: [0, 5, 0] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
        <span className="text-xs font-semibold uppercase tracking-wider">Scroll</span>
      </motion.button>
    </section>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;